// ErrorWheel — THE shared "something went wrong" mark for the web app.
//
// Gordon, 2026-07-25: "'Something went wrong' needs an image designed for it -
// red hued Wheel with an exclamation point in the middle?"
//
// WHY ONE COMPONENT AND NOT A FOURTH SVG: sandbox already carried three separate
// error images (ErrorImg.svg, SaveError.svg, wheelError.svg — all THREE with zero
// consumers) plus per-surface hand-rolled marks (a MUI ErrorOutline glyph in
// Explore, another in AddPayment, and a moderation error state borrowing the
// EMPTY-state illustration). Adding a fourth asset would reproduce exactly the
// drift this session spent the night removing. This component replaces them.
//
// ERROR IS NOT EMPTY. Use this ONLY when something broke — a failed fetch, a dead
// link, a 5xx. "Nothing here yet" keeps its own empty-state illustration, and a
// permission wall ("this wheel is private") keeps the hidden/lock mark: telling a
// user their private-wheel request is a system failure would be a lie.
//
// COMPOSED, NOT DRAWN: geometry comes from `brandWheelInner` via ./errorWheel.js —
// the same canonical renderer BrandWheelLoader uses. Colour is alpha tints of the
// ONE error red (#D92D20, tokens.field.errorText). See errorWheel.js for the
// contrast maths.
//
// SIZES — two variants, ONE ratio set. Both render the identical viewBox-120
// geometry and differ only in the rendered box, so the page mark and the section
// mark cannot drift apart the way three separate SVGs did:
//   <ErrorWheel variant="page" />     // 96px — route-level / full-surface errors
//   <ErrorWheel variant="section" />  // 56px — panels, tables, pickers, cards
//   <ErrorWheel size={44} />          // explicit px when a layout needs one
//
// MOTION: static by default apart from a single 240ms settle on mount. It NEVER
// rotates (wheels-dont-spin). prefers-reduced-motion => a still, complete mark.
//
// A11Y: never a colour-only signal — this mark always sits WITH the error text.
// The wrapper is role="img" with an accessible name (default "Error"), the SVG
// itself is aria-hidden, and nothing here is focusable. Where the surrounding
// container is already role="alert"/aria-live, pass `decorative` so assistive
// tech is not told about the failure twice.

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { errorWheelInner } from "./errorWheel";
import "./errorWheel.css";

const SIZES = { section: 56, page: 96 };

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const ErrorWheel = ({
  variant = "section",
  size,
  label = "Error",
  caption,
  decorative = false,
  className = "",
  style,
  ...rest
}) => {
  const px = size || SIZES[variant] || SIZES.section;
  // Read once per mount: a mid-render media-query flip is not worth a re-render,
  // and the settle is a one-shot anyway.
  const [reduce] = useState(prefersReducedMotion);
  // The settle runs for its duration then is dropped, so a re-render can never
  // replay it (the "recreate instead of mutate" trap that made marks flicker).
  const [settling, setSettling] = useState(!reduce);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!settling) return undefined;
    timerRef.current = setTimeout(() => setSettling(false), 260);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classes = ["ewl-wrap", variant === "page" ? "ewl-page" : "", className]
    .filter(Boolean)
    .join(" ");

  const a11y = decorative
    ? { "aria-hidden": "true" }
    : { role: "img", "aria-label": label };

  return (
    <div className={classes} style={style} {...a11y} {...rest}>
      <svg
        aria-hidden="true"
        focusable="false"
        className="ewl-svg"
        width={px}
        height={px}
        viewBox="0 0 120 120"
        data-testid="error-wheel"
        data-settle={settling ? "in" : undefined}
        dangerouslySetInnerHTML={{ __html: errorWheelInner() }}
      />
      {caption ? (
        <div className="ewl-caption" aria-hidden="true">
          {caption}
        </div>
      ) : null}
    </div>
  );
};

ErrorWheel.propTypes = {
  variant: PropTypes.oneOf(["section", "page"]),
  size: PropTypes.number,
  label: PropTypes.string,
  caption: PropTypes.string,
  decorative: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default ErrorWheel;
